import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { ActionCardComponent } from '@shared/components/action-card/action-card.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { TipoPadraoNaturezaEnum } from '@shared/enums/motor-fiscal/tipo-padrao-natureza.enum';
import { TipoPadraoNaturezaOperacaoStruct } from '@shared/services/structs/motor-fiscal/tipo-padrao-natureza-operacao.struct';
import { TornarPadraoDialogData } from './tornar-padrao-dialog-data';

export { TornarPadraoDialogData } from './tornar-padrao-dialog-data';

/** Resultado do diálogo: os tipos de padrão que a natureza passa a ter (lista vazia tira todos). */
export type TornarPadraoDialogResult = { confirm: boolean; tiposPadrao: TipoPadraoNaturezaEnum[] };

/**
 * "Definir como padrão": a natureza pode ser padrão de vários tipos do catálogo compatíveis com o tipo de operação
 * dela. Marcar um tipo que já é de outra natureza substitui (avisando antes); desmarcar tira o padrão.
 */
@Component({
  selector: 'app-tornar-padrao-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, ActionCardComponent, AlertCardComponent],
  templateUrl: './tornar-padrao-dialog.component.html',
  styleUrl: './tornar-padrao-dialog.component.css',
})
export class TornarPadraoDialogComponent implements OnInit {
  public selecionados = new Set<number>();
  public tiposPadraoDisponiveis: TipoPadraoNaturezaOperacaoStruct[] = [];
  public readonly tipoPadraoIcones: Record<number, string> = {
    [TipoPadraoNaturezaEnum.NFeEntrada]: 'login',
    [TipoPadraoNaturezaEnum.Venda]: 'shopping_cart',
    [TipoPadraoNaturezaEnum.NFCe]: 'receipt',
    [TipoPadraoNaturezaEnum.NFeSaida]: 'description',
  };

  private iniciais = new Set<number>();

  constructor(
    public dialogRef: MatDialogRef<TornarPadraoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TornarPadraoDialogData
  ) {}

  ngOnInit(): void {
    this.tiposPadraoDisponiveis = (this.data.tiposPadrao ?? [])
      .filter(tipo => tipo.idTipoOperacao === this.data.natureza.idTipoOperacao);
    this.iniciais = new Set<number>(this.data.natureza.tiposPadrao ?? []);
    this.selecionados = new Set<number>(this.iniciais);
  }

  public isSelecionado(tipo: TipoPadraoNaturezaOperacaoStruct): boolean {
    return this.selecionados.has(tipo.idTipoPadraoNaturezaOperacao);
  }

  public alternar(tipo: TipoPadraoNaturezaOperacaoStruct): void {
    const id = tipo.idTipoPadraoNaturezaOperacao;
    if (this.selecionados.has(id)) {
      this.selecionados.delete(id);
    } else {
      this.selecionados.add(id);
    }
  }

  /** Natureza que hoje é o padrão do tipo, quando é outra (esta já é o padrão dos tipos iniciais). */
  public nomeNaturezaAtual(tipo: TipoPadraoNaturezaOperacaoStruct): string | null {
    if (this.iniciais.has(tipo.idTipoPadraoNaturezaOperacao)) return null;
    return tipo.idNaturezaOperacaoAtual != null ? tipo.nomeNaturezaOperacaoAtual : null;
  }

  /** Avisos do que muda ao confirmar: outra natureza que perde o padrão e padrão que esta natureza deixa. */
  public get avisos(): string[] {
    const avisos: string[] = [];
    for (const tipo of this.tiposPadraoDisponiveis) {
      const id = tipo.idTipoPadraoNaturezaOperacao;
      const atual = this.nomeNaturezaAtual(tipo);
      if (this.selecionados.has(id) && atual) {
        avisos.push(`Ao confirmar, ${atual} deixa de ser o ${tipo.nome}.`);
      }
      if (!this.selecionados.has(id) && this.iniciais.has(id)) {
        avisos.push(`Esta natureza deixa de ser o ${tipo.nome}.`);
      }
    }
    return avisos;
  }

  /** Só confirma quando a seleção difere da atual; desmarcar tudo é permitido (tira os padrões). */
  public get podeConfirmar(): boolean {
    if (this.selecionados.size !== this.iniciais.size) return true;
    return [...this.selecionados].some(id => !this.iniciais.has(id));
  }

  public salvar(): void {
    if (!this.podeConfirmar) return;

    const result: TornarPadraoDialogResult = {
      confirm: true,
      tiposPadrao: [...this.selecionados].sort((a, b) => a - b),
    };
    this.dialogRef.close(result);
  }

  public cancelar(): void {
    this.dialogRef.close({ confirm: false, tiposPadrao: [] } as TornarPadraoDialogResult);
  }
}
